// src/features/records/components/GalleryView/components/MediaLightbox/components/ImageViewer.js
import React, { useState, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import styles from './ImageViewer.module.css';

/**
 * ImageViewer - Displays image with loading states and error handling
 * Supports zoom and pan functionality (to be enhanced with gestures)
 */
const ImageViewer = ({ 
  item, 
  url, 
  previewUrl,
  transform,
  dragOffset,
  isDragging, 
  onLoad, 
  onError,
  isActive,
  canLoad
}) => {
  const { t } = useTranslation();
  const [displayUrl, setDisplayUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const displayKindRef = useRef(null);
  const hasDisplayedRef = useRef(false);
  const previewFailedRef = useRef(false);
  const fullFailedRef = useRef(false);
  const onLoadRef = useRef(onLoad);
  const onErrorRef = useRef(onError);
  
  const setDisplayed = useCallback((nextUrl, kind) => {
    displayKindRef.current = kind;
    setDisplayUrl(nextUrl);
    if (!hasDisplayedRef.current) {
      hasDisplayedRef.current = true;
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    onLoadRef.current = onLoad;
  }, [onLoad]);

  useEffect(() => {
    onErrorRef.current = onError;
  }, [onError]);
  
  useEffect(() => {
    if (!canLoad || (!url && !previewUrl)) return;
    
    let cancelled = false;
    previewFailedRef.current = false;
    fullFailedRef.current = false;
    const hasDistinctPreview = Boolean(previewUrl && previewUrl !== url);
    
    // Reset error for every attempt; only show spinner before first paint
    setError(false);
    if (!hasDisplayedRef.current) setLoading(true);
    
    const loadImage = (src, kind) => {
      if (!src) return;
      const img = new Image();
      img.src = src;
      
      const finalize = async () => {
        if (cancelled) return;
        try {
          if ('decode' in img) await img.decode();
        } catch {
          // If decode fails, we still show the image.
        }
        if (cancelled) return;
        if (kind === 'preview' && displayKindRef.current === 'full') return;
        setDisplayed(src, kind);
        setError(false);
        onLoadRef.current?.();
      };
      
      img.onload = finalize;
      
      img.onerror = () => {
        if (cancelled) return;
        if (kind === 'preview') {
          previewFailedRef.current = true;
          if (!url && !hasDisplayedRef.current) {
            setError(true);
            setLoading(false);
            onErrorRef.current?.();
          }
          if (fullFailedRef.current && !hasDisplayedRef.current) {
            setError(true);
            setLoading(false);
          }
          return;
        }
        
        fullFailedRef.current = true;
        if (!hasDisplayedRef.current && (!hasDistinctPreview || previewFailedRef.current)) {
          setError(true);
          setLoading(false);
        }
        onErrorRef.current?.();
      };
    };
    
    if (hasDistinctPreview) {
      loadImage(previewUrl, 'preview');
    }
    
    if (url) {
      loadImage(url, 'full');
    }
    
    return () => {
      cancelled = true;
    };
  }, [previewUrl, url, canLoad, setDisplayed]);
  
  return (
    <div className={styles.container}>
      {/* Loading spinner */}
      {loading && (
        <div className={styles.loading}>
          <Loader2 className={styles.spinner} size={48} />
        </div>
      )}
      
      {/* Error state */}
      {error && (
        <div className={styles.error}>
          <p>{t('records.views.gallery.errors.image_load_failed')}</p>
        </div>
      )}
      
      {/* Image */}
      {!error && displayUrl && (
        <img
          src={displayUrl}
          alt=""
          className={styles.image}
          style={{
            transform: `translate3d(${transform.x + (dragOffset?.x || 0)}px, ${transform.y + (dragOffset?.y || 0)}px, 0) scale(${transform.scale})`,
            opacity: loading ? 0 : 1,
            transition: isDragging ? 'none' : undefined
          }}
          draggable={false}
          fetchPriority={isActive ? 'high' : 'auto'}
        />
      )}
    </div>
  );
};

ImageViewer.propTypes = {
  item: PropTypes.shape({
    id: PropTypes.string.isRequired,
    type: PropTypes.string,
    width: PropTypes.number,
    height: PropTypes.number
  }).isRequired,
  url: PropTypes.string,
  previewUrl: PropTypes.string,
  transform: PropTypes.shape({
    scale: PropTypes.number,
    x: PropTypes.number,
    y: PropTypes.number
  }),
  dragOffset: PropTypes.shape({
    x: PropTypes.number,
    y: PropTypes.number
  }),
  isDragging: PropTypes.bool,
  onLoad: PropTypes.func,
  onError: PropTypes.func,
  isActive: PropTypes.bool,
  canLoad: PropTypes.bool
};

ImageViewer.defaultProps = {
  transform: { scale: 1, x: 0, y: 0 },
  dragOffset: { x: 0, y: 0 },
  isDragging: false,
  isActive: true,
  canLoad: true
};

export default ImageViewer;
